import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import {
  getPersonDetails,
  getPersonFinancialFootprint,
  touchEntityVisit,
} from '../services/apiClient'
import SinceLastVisitBanner from '../components/profile/SinceLastVisitBanner'
import { PersonDetails, PersonFinancialFootprint as PersonFinancialFootprintData } from '../types'
import PersonNetworkTab from '../components/profile/network/PersonNetworkTab'
import { Container, CountryFlagIcon } from '../components/ui'
import type { TabDef } from '../components/ui'
import { Tabs } from '../components/ui'
import WatchlistControl from '../components/WatchlistControl'
import {
  PersonOverviewStrip,
  PersonAffiliationsView,
  PersonActivity,
  PersonFinancialFootprint,
  groupByCompany,
} from '../components/profile/person'
import ProfileActionLinks, { PrintCitation } from '../components/profile/ProfileActionLinks'
import { personSourceCitation } from '../utils/sourceCitation'
import { dataCvrPersonUrl } from '../utils/cvrRegisterLinks'
import {
  detectCountry,
  formatAddressCountryLabel,
  formatProtectedAddressNote,
  stripTrailingCountryFromAddress,
} from '../utils/format'
import { navigateBackOrHome } from '../utils/appNavigation'
import { usePageTitle } from '../hooks/usePageTitle'
import { collectAllPersonActivity } from '../utils/personRecentActivity'
import { personVisitLabel } from '../utils/visitLabels'

/**
 * Person profile — tabbed counterpart to CompanyProfilePage.
 *
 * Tabs:
 *   Overblik     — role breakdown + recent activity strip, active affiliations
 *   Forbindelser — what is worth knowing + the network as list or map (lazy-loaded)
 *   Aktivitet    — full activity history grouped by year
 *   Tidl. roller — historic affiliations
 */

// ── tab setup ────────────────────────────────────────────────────────────────

type TabId = 'overblik' | 'forbindelser' | 'aktivitet' | 'historik'
const TAB_IDS: TabId[] = ['overblik', 'forbindelser', 'aktivitet', 'historik']

function readTab(raw: string | null): TabId {
  if (raw === 'aktive') return 'overblik'
  return (TAB_IDS as string[]).includes(raw ?? '') ? (raw as TabId) : 'overblik'
}

// ── page ─────────────────────────────────────────────────────────────────────

const PersonDetailPage: React.FC = () => {
  const { enhedsNummer } = useParams<{ enhedsNummer: string }>()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const [person, setPerson]   = useState<PersonDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)

  const [footprint, setFootprint]               = useState<PersonFinancialFootprintData | null>(null)
  const [footprintLoading, setFootprintLoading] = useState(true)

  const activeTab: TabId = readTab(searchParams.get('tab'))

  const handleTabChange = (id: string) => {
    const next = readTab(id)
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next === 'overblik') {
          params.delete('tab')
        } else {
          params.set('tab', next)
        }
        return params
      },
      { replace: true }
    )
  }

  // ── visit: when did this user last look (before this session)? ────────────
  // Recorded once the person has loaded, so »Senest åbnet« gets the name.
  const [previousVisitAt, setPreviousVisitAt] = useState<string | null>(null)
  const visitedRef = useRef<string | null>(null)
  const currentIdRef = useRef(enhedsNummer)
  currentIdRef.current = enhedsNummer
  useEffect(() => {
    setPreviousVisitAt(null)
  }, [enhedsNummer])

  // ── person fetch ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enhedsNummer) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setPerson(null)
    getPersonDetails(enhedsNummer)
      .then((details) => { if (!cancelled) setPerson(details) })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Kunne ikke hente personoplysninger.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [enhedsNummer])

  // ── financial footprint — independent, non-blocking ────────────────────────
  useEffect(() => {
    if (!enhedsNummer) return
    let cancelled = false
    setFootprintLoading(true)
    setFootprint(null)
    getPersonFinancialFootprint(enhedsNummer)
      .then((data) => { if (!cancelled) setFootprint(data) })
      .catch(() => { if (!cancelled) setFootprint(null) })
      .finally(() => { if (!cancelled) setFootprintLoading(false) })
    return () => { cancelled = true }
  }, [enhedsNummer])

  const activeEntries   = useMemo(() => groupByCompany(person?.affiliations ?? [], true),  [person])
  const historicEntries = useMemo(() => groupByCompany(person?.affiliations ?? [], false), [person])

  useEffect(() => {
    if (!enhedsNummer || !person || String(person.enhedsNummer) !== enhedsNummer) return
    if (visitedRef.current === enhedsNummer) return
    visitedRef.current = enhedsNummer
    const home = detectCountry(person.adresse)
    const label = personVisitLabel(
      person.navn || `Person ${person.enhedsNummer}`,
      home.isForeign ? home.countryCode : null,
      activeEntries.length
    )
    void touchEntityVisit('person', enhedsNummer, label).then((at) => {
      if (currentIdRef.current === enhedsNummer) setPreviousVisitAt(at)
    })
  }, [enhedsNummer, person, activeEntries])
  const activeCount   = activeEntries.reduce((n, e) => n + e.roles.length, 0)
  const historicCount = historicEntries.reduce((n, e) => n + e.roles.length, 0)
  const allActivity = useMemo(
    () => (person ? collectAllPersonActivity(person.affiliations) : []),
    [person]
  )
  const activityCount = allActivity.length
  const changeDates = useMemo(() => allActivity.map((row) => row.date), [allActivity])

  const pageTitle = useMemo(() => {
    if (loading) return 'Person'
    if (error || !person) return 'Person ikke fundet'
    return person.navn || `Person ${person.enhedsNummer}`
  }, [loading, error, person])
  usePageTitle(pageTitle)

  // ── loading / error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Container className="py-16">
        <p className="text-[13px] font-semibold text-ink-500">
          Henter personoplysninger…
        </p>
      </Container>
    )
  }

  if (error || !person) {
    return (
      <Container className="py-16">
        <BackLink />
        <h1 className="text-2xl text-ink mt-6">Kunne ikke vise personen</h1>
        <p className="mt-3 text-sm text-signal-700 bg-signal-50 rounded-md px-3 py-2">
          {error || 'Personen blev ikke fundet.'}
        </p>
      </Container>
    )
  }

  // ── derived values ─────────────────────────────────────────────────────────
  const country = detectCountry(person.adresse)
  const enhedsStr = String(person.enhedsNummer ?? '').trim()
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${location.pathname}${location.search}`
      : `${location.pathname}${location.search}`
  const datacvrHref = dataCvrPersonUrl(person.enhedsNummer)
  const citation = personSourceCitation(
    person.navn || `Person ${enhedsStr}`,
    enhedsStr,
    shareUrl,
  )

  const tabs: TabDef[] = [
    { id: 'overblik',      label: 'Overblik' },
    { id: 'forbindelser',  label: 'Forbindelser' },
    {
      id: 'aktivitet',
      label: 'Aktivitet',
      count: activityCount > 0 ? activityCount : null,
    },
    {
      id: 'historik',
      label: 'Tidl. roller',
      count: historicCount > 0 ? historicCount : null,
    },
  ]

  return (
    <Container className="pt-5 pb-16">
      <BackLink />

      {/* ── Identity header ─────────────────────────────────────────────── */}
      <header className="mt-3.5" data-testid="person-profile-head">
        <div
          className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4"
          data-testid="profile-title-row"
        >
          <div className="min-w-0 flex-1">
            <h1 className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[26px] sm:text-[34px] leading-[1.1] text-ink">
              {person.navn || <em className="text-ink-400">Uden navn</em>}
              {country.isForeign && country.countryCode && (
                <CountryFlagIcon code={country.countryCode} size="lg" />
              )}
            </h1>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-[14.5px] text-ink-500">
              <span className="font-semibold text-ink tabular-nums">Person · enhedsnr. {enhedsStr || '—'}</span>
              {person.isAddressConfidential ? (
                <span>{formatProtectedAddressNote(person.adresse)}</span>
              ) : (
                person.adresse && (
                  <span>
                    {stripTrailingCountryFromAddress(
                      formatAddressCountryLabel(person.adresse),
                      country.isForeign ? country.countryCode : undefined
                    )}
                  </span>
                )
              )}
            </div>
          </div>
          <ProfileActionLinks
            shareUrl={shareUrl}
            citation={citation}
            connectionHref={`/forbindelse?fra=person:${enhedsStr}&fraNavn=${encodeURIComponent(person.navn || enhedsStr)}`}
            datacvrHref={datacvrHref}
            datacvrAriaLabel="Åbn person på CVR.dk i nyt vindue"
          >
            <WatchlistControl
              headerButton
              entityType="person"
              entityId={String(person.enhedsNummer)}
              displayName={person.navn || `Person ${person.enhedsNummer}`}
              countryCode={country.isForeign ? country.countryCode : null}
            />
          </ProfileActionLinks>
        </div>

        <PersonFinancialFootprint
          footprint={footprint}
          loading={footprintLoading}
          personName={person.navn || `Person ${person.enhedsNummer}`}
        />

        <SinceLastVisitBanner
          previousVisitAt={previousVisitAt}
          changeDates={changeDates}
          onShow={() => handleTabChange('aktivitet')}
        />
        <Tabs
          dense
          tabs={tabs}
          active={activeTab}
          onChange={handleTabChange}
          ariaLabel="Personsektioner"
          className="mt-[18px]"
        />
        <PrintCitation citation={citation} />
      </header>

      {/* ── Overblik ──────────────────────────────────────────────────────── */}
      <TabPanel id="overblik" active={activeTab}>
        <div className="space-y-6">
          <PersonOverviewStrip affiliations={person.affiliations} />

          {activeCount > 0 ? (
            <PersonAffiliationsView
              entries={activeEntries}
              sectionTitle="Nuværende virksomhedsroller"
              sectionDescription="Selskaber personen er aktivt registreret i."
              borderTop={false}
            />
          ) : (
            <p className="text-sm text-ink-500">Ingen aktive roller registreret.</p>
          )}
        </div>
      </TabPanel>

      {/* ── Forbindelser ──────────────────────────────────────────────────── */}
      <TabPanel id="forbindelser" active={activeTab}>
        <PersonNetworkTab
          enhedsNummer={enhedsNummer ?? ''}
          personName={person.navn}
          enabled={activeTab === 'forbindelser'}
        />
      </TabPanel>

      {/* ── Aktivitet ─────────────────────────────────────────────────────── */}
      <TabPanel id="aktivitet" active={activeTab}>
        <PersonActivity affiliations={person.affiliations} />
      </TabPanel>

      {/* ── Tidl. roller ──────────────────────────────────────────────────── */}
      <TabPanel id="historik" active={activeTab}>
        {historicCount > 0 ? (
          <PersonAffiliationsView
            entries={historicEntries}
            variant="historic"
            sectionTitle={`Tidligere roller (${historicCount})`}
            sectionDescription="Selskaber personen tidligere var registreret i."
          />
        ) : (
          <p className="mt-6 text-sm text-ink-500">Ingen tidligere roller registreret.</p>
        )}
      </TabPanel>
    </Container>
  )
}

// ── sub-components ────────────────────────────────────────────────────────────

interface TabPanelProps {
  id: TabId
  active: TabId
  children: React.ReactNode
}

const TabPanel: React.FC<TabPanelProps> = ({ id, active, children }) => {
  const hidden = id !== active
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${id}`}
      aria-labelledby={`tab-${id}`}
      hidden={hidden}
      className={hidden ? '' : 'mt-5'}
    >
      {!hidden && children}
    </div>
  )
}

const BackLink: React.FC = () => {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigateBackOrHome(navigate)}
      className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink bg-transparent border-0 p-0 cursor-pointer text-left print:hidden"
    >
      <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
      Tilbage
    </button>
  )
}

export default PersonDetailPage
